import { ChevronDown } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { CategoryItem } from "../../types";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

interface ExtractPagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: CategoryItem[];
  selectedPageIds: string[];
  onExtract: (category: CategoryItem, documentName: string) => void;
}

export const ExtractPagesDialog: FC<ExtractPagesDialogProps> = ({
  open,
  onOpenChange,
  categories,
  selectedPageIds,
  onExtract,
}) => {
  const selectedPageCount = selectedPageIds.length;
  const { formatMessage } = useIntl();
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (open) {
      setDocumentName("");
      setCategoryId(null);
      setIsPending(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!documentName.trim() || categoryId === null) return;
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;
    setIsPending(true);
    onExtract(category, documentName);
  };

  const isFormValid = documentName.trim() !== "" && categoryId !== null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="docList.multiSelect.extractDialog.title"
              defaultMessage="Extract to New Document"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="docList.multiSelect.extractDialog.description"
              defaultMessage="Create a new document from {count} selected {count, plural, one {page} other {pages}}."
              values={{ count: selectedPageCount }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="document-name">
              <FormattedMessage
                id="docList.multiSelect.extractDialog.documentName"
                defaultMessage="Document name"
              />
            </Label>
            <Input
              id="document-name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder={formatMessage({
                id: "docList.multiSelect.extractDialog.documentNamePlaceholder",
                defaultMessage: "Enter document name",
              })}
              disabled={isPending}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>
              <FormattedMessage
                id="docList.multiSelect.extractDialog.category"
                defaultMessage="Category"
              />
            </Label>
            <Popover
              open={openCategoryDropdown}
              onOpenChange={setOpenCategoryDropdown}
              modal={true}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCategoryDropdown}
                  className="w-full justify-between uppercase"
                  disabled={isPending}
                >
                  {categoryId !== null
                    ? categories.find((c) => c.id === categoryId)?.name
                    : formatMessage({
                        id: "docList.multiSelect.extractDialog.categoryPlaceholder",
                        defaultMessage: "Select category",
                      })}
                  <ChevronDown className="opacity-50 w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[465px] p-0 cursor-pointer">
                <Command
                  filter={(_, search, keywords = []) => {
                    const extendValue = keywords.join(" ");
                    if (
                      extendValue.toLowerCase().includes(search.toLowerCase())
                    ) {
                      return 1;
                    }
                    return 0;
                  }}
                >
                  <CommandInput
                    placeholder={formatMessage({
                      id: "docList.multiSelect.extractDialog.categoryPlaceholder",
                      defaultMessage: "Select category",
                    })}
                  />
                  <CommandList className="max-h-[200px]">
                    <CommandEmpty>
                      <FormattedMessage
                        id="common.noResults"
                        defaultMessage="No results found"
                      />
                    </CommandEmpty>
                    <CommandGroup>
                      {categories.map((category) => (
                        <CommandItem
                          key={category.id}
                          value={String(category.id)}
                          keywords={[category.name]}
                          onSelect={(currentValue) => {
                            const selectedCategory = categories.find(
                              (c) => String(c.id) === currentValue
                            );
                            if (selectedCategory) {
                              setCategoryId(selectedCategory.id);
                              setOpenCategoryDropdown(false);
                            }
                          }}
                          className="uppercase"
                        >
                          {category.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleConfirm} disabled={!isFormValid || isPending}>
            {isPending ? (
              <FormattedMessage
                id="docList.multiSelect.extractDialog.creating"
                defaultMessage="Creating..."
              />
            ) : (
              <FormattedMessage
                id="docList.multiSelect.extractDialog.create"
                defaultMessage="Create Document"
              />
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
